import React from 'react';
import { DatePicker, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import InputFilterItem from '../../../../components/InputFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import type { AddressErrorType } from '../graphql/addressErrorRecordGraphql';
import { ADDRESS_ERROR_HANDLE_FLAG } from '../graphql/addressErrorRecordGraphql';

const { RangePicker } = DatePicker;

export interface FilterOptions {
  originalZipcode?: string;
  originalStreetAddress?: string;
  errorType?: AddressErrorType;
  handleFlag?: number;
  createdRange?: [Dayjs | null, Dayjs | null];
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
  originalZipcode: undefined,
  originalStreetAddress: undefined,
  errorType: undefined,
  handleFlag: undefined,
  createdRange: undefined,
};

interface FilterAreaProps {
  filters: FilterOptions;
  setFilters: (filters: FilterOptions) => void;
}

const FilterArea: React.FC<FilterAreaProps> = ({ filters, setFilters }) => {
  const { t } = useTranslation();

  const handleTextChange = (key: 'originalZipcode' | 'originalStreetAddress', value: string | undefined) => {
    const processed = typeof value === 'string' && value.trim() === '' ? undefined : value;
    setFilters({ ...filters, [key]: processed });
  };

  const columnLabels = [
    t('mapTool.addressErrorRecord.filters.originalZipcode'),
    t('mapTool.addressErrorRecord.filters.originalStreetAddress'),
    t('mapTool.addressErrorRecord.filters.errorType'),
    t('mapTool.addressErrorRecord.filters.handleFlag'),
    t('mapTool.addressErrorRecord.filters.createdRange'),
  ];
  const labelClassName = getSmartLabelClassName(columnLabels);

  const errorTypeOptions: { value: AddressErrorType; label: string }[] = [
    { value: 'HOUSE_NUM_MISMATCH', label: t('enum.addressErrorType.HOUSE_NUM_MISMATCH') as string },
    { value: 'INVALID_ADDRESS', label: t('enum.addressErrorType.INVALID_ADDRESS') as string },
    { value: 'PRECISION_LACK', label: t('enum.addressErrorType.PRECISION_LACK') as string },
    { value: 'OTHER', label: t('enum.addressErrorType.OTHER') as string },
  ];

  const handleFlagOptions: { value: number; label: string }[] = [
    { value: ADDRESS_ERROR_HANDLE_FLAG.UNHANDLED, label: t('enum.addressErrorHandleFlag.0') as string },
    { value: ADDRESS_ERROR_HANDLE_FLAG.HANDLED_SUCCESS, label: t('enum.addressErrorHandleFlag.1') as string },
    { value: ADDRESS_ERROR_HANDLE_FLAG.HANDLED_FAILED, label: t('enum.addressErrorHandleFlag.2') as string },
  ];

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
        <div className="min-w-0">
          <InputFilterItem
            label={t('mapTool.addressErrorRecord.filters.originalZipcode')}
            value={filters.originalZipcode ?? ''}
            onChange={(e) => handleTextChange('originalZipcode', e.target.value)}
            spanClassName={labelClassName}
            placeholder={t('mapTool.addressErrorRecord.placeholders.originalZipcodeExact') as string}
          />
        </div>
        <div className="min-w-0">
          <InputFilterItem
            label={t('mapTool.addressErrorRecord.filters.originalStreetAddress')}
            value={filters.originalStreetAddress ?? ''}
            onChange={(e) => handleTextChange('originalStreetAddress', e.target.value)}
            spanClassName={labelClassName}
            placeholder={t('mapTool.addressErrorRecord.placeholders.originalStreetAddressFuzzy') as string}
          />
        </div>
        <div className="min-w-0 flex items-center gap-2">
          <span className={labelClassName}>
            {t('mapTool.addressErrorRecord.filters.errorType')}
          </span>
          <Select
            className="flex-1"
            allowClear
            placeholder={t('mapTool.addressErrorRecord.placeholders.errorType') as string}
            value={filters.errorType}
            onChange={(v) => setFilters({ ...filters, errorType: (v as AddressErrorType) || undefined })}
            options={errorTypeOptions}
          />
        </div>
        <div className="min-w-0 flex items-center gap-2">
          <span className={labelClassName}>
            {t('mapTool.addressErrorRecord.filters.handleFlag')}
          </span>
          <Select
            className="flex-1"
            allowClear
            placeholder={t('mapTool.addressErrorRecord.placeholders.handleFlag') as string}
            value={filters.handleFlag}
            onChange={(v) => setFilters({ ...filters, handleFlag: v ?? undefined })}
            options={handleFlagOptions}
          />
        </div>
        <div className="min-w-0 flex items-center gap-2">
          <span className={labelClassName}>
            {t('mapTool.addressErrorRecord.filters.createdRange')}
          </span>
          <RangePicker
            className="flex-1"
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            value={filters.createdRange}
            onChange={(dates) =>
              setFilters({
                ...filters,
                createdRange: dates ? (dates as [Dayjs | null, Dayjs | null]) : undefined,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default FilterArea;
